import { ChangeDetectionStrategy, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon';

export type InputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'date'
  | 'search'
  | 'textarea';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ui-input',
  imports: [IconComponent],
  styleUrl: './input.scss',
  templateUrl: './input.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  /** Libellé affiché au-dessus du champ. */
  @Input() label = '';

  /** Type de champ (dont 'textarea'). */
  @Input() type: InputType = 'text';

  /** Texte indicatif affiché quand le champ est vide. */
  @Input() placeholder = '';

  /** Texte d'aide affiché sous le champ. */
  @Input() hint = '';

  /** Message d'erreur affiché sous le champ. */
  @Input() error = '';

  /** Attribut `name` HTML. */
  @Input() name = '';

  /** Marque le champ comme requis (attribut HTML uniquement). */
  @Input() required = false;

  /** Désactive le champ. */
  @Input() disabled = false;

  /** Identifiant HTML (lié au libellé). */
  @Input() id = '';

  /** Affiche un bouton afficher/masquer (uniquement pour type=password). */
  @Input() togglePassword = false;

  /** Nombre de lignes visibles (textarea). */
  @Input() rows = 4;

  protected value = '';
  protected passwordVisible = false;

  protected get effectiveType(): string {
    if (this.type === 'password' && this.passwordVisible) {
      return 'text';
    }
    return this.type;
  }

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  protected togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }
}
